import type { OesFeatureIcon } from "./oesModuleIcons";

export interface ModuleFeature {
  title: string;
  icon: OesFeatureIcon;
  description?: string;
  items?: string[];
}

export interface OesModule {
  id: string;
  title: string;
  description: string;
  features: ModuleFeature[];
}

export const oesModules: OesModule[] = [
  {
    id: "admin",
    title: "Admin Panel",
    description:
      "Central control for institutions — configure exams, manage users, schedule sessions, and monitor live activity.",
    features: [
      {
        title: "Exam Configuration",
        icon: "exam-config",
        description:
          "Create exams with custom duration, sections, marking schemes, pass criteria, and attempt limits.",
      },
      {
        title: "User & Role Management",
        icon: "user-roles",
        description:
          "Manage students, invigilators, evaluators, and admins with granular role-based permissions.",
      },
      {
        title: "Institution Setup",
        icon: "institution",
        description:
          "Configure campuses, departments, batches, courses, and academic year structures.",
      },
      {
        title: "Exam Scheduling",
        icon: "scheduling",
        description:
          "Schedule exams with start/end windows, slot booking, and automated reminders to candidates.",
      },
      {
        title: "Bulk Registration",
        icon: "bulk-register",
        description:
          "Import students via CSV/Excel, assign exam batches, and auto-generate login credentials.",
      },
      {
        title: "Live Exam Monitor",
        icon: "live-monitor",
        description:
          "Real-time dashboard showing active sessions, submissions, malpractice flags, and connectivity status.",
      },
      {
        title: "Notification Engine",
        icon: "notifications",
        description:
          "Send exam alerts, hall tickets, and result notifications via email, SMS, and in-app messages.",
      },
      {
        title: "Reports Dashboard",
        icon: "reports",
        description:
          "Access attendance, performance, subject-wise analytics, and institution-level summary reports.",
      },
      {
        title: "Integration APIs",
        icon: "api",
        description:
          "Connect with existing LMS, ERP, or SIS systems through REST APIs and SSO authentication.",
      },
      {
        title: "Audit & Compliance",
        icon: "audit",
        description:
          "Maintain activity logs, exam attempt records, and tamper-proof audit trails for accreditation.",
      },
    ],
  },
  {
    id: "question-bank",
    title: "Question Bank",
    description:
      "Build and manage a rich repository of questions across subjects, difficulty levels, and question types.",
    features: [
      {
        title: "MCQ Questions",
        icon: "mcq",
        description:
          "Create single and multiple-choice questions with configurable options, images, and explanations.",
      },
      {
        title: "Descriptive Questions",
        icon: "descriptive",
        description:
          "Add short-answer and long-answer questions with word limits and file upload support.",
      },
      {
        title: "True/False & Fill Blanks",
        icon: "question-types",
        description:
          "Support varied formats including match-the-following, fill-in-the-blanks, and assertion-reason.",
      },
      {
        title: "Question Categories",
        icon: "categories",
        description:
          "Organise questions by subject, topic, sub-topic, and syllabus unit for easy retrieval.",
      },
      {
        title: "Difficulty Levels",
        icon: "difficulty",
        description:
          "Tag questions as easy, medium, or hard to enable balanced paper generation.",
      },
      {
        title: "Image & Media Support",
        icon: "media",
        description:
          "Embed diagrams, charts, audio clips, and video-based questions in the exam interface.",
      },
      {
        title: "Bulk Import/Export",
        icon: "import-export",
        description:
          "Import questions from Excel, Word, or QTI format and export banks for backup or sharing.",
      },
      {
        title: "Review Workflow",
        icon: "review",
        description:
          "Subject experts review, approve, and flag questions before they enter the active bank.",
      },
      {
        title: "Random Question Pool",
        icon: "random-pool",
        description:
          "Define pools from which the system randomly selects questions for each candidate.",
      },
      {
        title: "Version Control",
        icon: "version",
        description:
          "Track question edits, maintain revision history, and archive outdated items safely.",
      },
    ],
  },
  {
    id: "exam-engine",
    title: "Exam Engine",
    description:
      "Robust assessment engine that delivers secure, timed exams with flexible paper structures.",
    features: [
      {
        title: "Timed Assessments",
        icon: "timer",
        description:
          "Set overall and section-wise timers with countdown display and warning alerts before expiry.",
      },
      {
        title: "Section-wise Papers",
        icon: "sections",
        description:
          "Build multi-section papers with mandatory sections, optional choices, and section timers.",
      },
      {
        title: "Auto Question Shuffle",
        icon: "shuffle",
        description:
          "Randomise question order and option sequence for each candidate to reduce copying.",
      },
      {
        title: "Negative Marking",
        icon: "negative-marking",
        description:
          "Configure negative marks per question type with partial marking support where applicable.",
      },
      {
        title: "Auto Submit on Timeout",
        icon: "auto-submit",
        description:
          "Automatically submit answers when time expires with grace period configuration.",
      },
      {
        title: "Answer Review & Marking",
        icon: "mark-review",
        description:
          "Allow students to flag questions for review and navigate freely within allowed sections.",
      },
      {
        title: "Practice & Mock Tests",
        icon: "practice",
        description:
          "Offer unlimited practice tests and timed mock exams with instant feedback options.",
      },
      {
        title: "Offline Exam Mode",
        icon: "offline",
        description:
          "Conduct exams in low-connectivity environments with sync-on-reconnect capability.",
      },
      {
        title: "Adaptive Testing",
        icon: "adaptive",
        description:
          "Adjust question difficulty dynamically based on candidate performance during the exam.",
      },
      {
        title: "Multi-language Support",
        icon: "language",
        description:
          "Deliver exams in multiple languages with bilingual question display where required.",
      },
    ],
  },
  {
    id: "proctoring",
    title: "Proctoring & Security",
    description:
      "Advanced anti-malpractice controls for fair, secure online examinations at scale.",
    features: [
      {
        title: "AI Proctoring",
        icon: "ai-proctor",
        description:
          "AI-powered monitoring detects suspicious behaviour, multiple faces, and absence from frame.",
      },
      {
        title: "Webcam Monitoring",
        icon: "webcam",
        description:
          "Continuous webcam capture with periodic snapshots and live invigilator view.",
      },
      {
        title: "Tab Switch Detection",
        icon: "tab-switch",
        description:
          "Detect and log tab switches, window minimisation, and unauthorised application usage.",
      },
      {
        title: "Full Screen Lock",
        icon: "fullscreen",
        description:
          "Force full-screen mode during the exam with warnings and auto-termination on violations.",
      },
      {
        title: "IP & Device Restriction",
        icon: "device-lock",
        description:
          "Restrict exams to approved IP ranges, devices, and browsers for controlled environments.",
      },
      {
        title: "Browser Lockdown",
        icon: "browser-lock",
        description:
          "Disable copy-paste, right-click, print screen, and developer tools during active sessions.",
      },
      {
        title: "Identity Verification",
        icon: "identity",
        description:
          "Verify candidates via photo ID upload, face match, and OTP-based authentication.",
      },
      {
        title: "Session Recording",
        icon: "recording",
        description:
          "Record screen and webcam feeds for post-exam review and dispute resolution.",
      },
      {
        title: "Malpractice Alerts",
        icon: "alerts",
        description:
          "Real-time alerts to invigilators when suspicious activity is detected during the exam.",
      },
      {
        title: "Secure Question Delivery",
        icon: "secure-delivery",
        description:
          "Encrypted question papers with time-locked release and watermarking per candidate.",
      },
    ],
  },
  {
    id: "student",
    title: "Student Portal",
    description:
      "Intuitive interface for candidates to access exams, view schedules, and check results.",
    features: [
      {
        title: "Exam Dashboard",
        icon: "dashboard",
        description:
          "View upcoming, ongoing, and completed exams with countdown timers and status indicators.",
      },
      {
        title: "Hall Ticket Download",
        icon: "hall-ticket",
        description:
          "Download printable hall tickets with photo, exam centre, seat number, and instructions.",
      },
      {
        title: "System Compatibility Check",
        icon: "compat-check",
        description:
          "Pre-exam browser, webcam, microphone, and internet speed verification before start.",
      },
      {
        title: "Live Exam Interface",
        icon: "exam-interface",
        description:
          "Clean, distraction-free UI with question palette, timer, and section navigation.",
      },
      {
        title: "Auto-save Answers",
        icon: "autosave",
        description:
          "Answers saved automatically every few seconds to prevent data loss on connectivity issues.",
      },
      {
        title: "Result View",
        icon: "results",
        description:
          "Check scores, rank, percentile, and subject-wise breakdown after result publication.",
      },
      {
        title: "Answer Key & Solutions",
        icon: "answer-key",
        description:
          "Access official answer keys and detailed solutions when enabled by the institution.",
      },
      {
        title: "Performance Analytics",
        icon: "analytics",
        description:
          "Track improvement over time with topic-wise strength and weakness analysis.",
      },
      {
        title: "Certificate Download",
        icon: "certificate",
        description:
          "Download digital certificates and scorecards with QR verification.",
      },
      {
        title: "Mobile Exam Access",
        icon: "mobile",
        description:
          "Responsive web and mobile app support for exams designed for handheld devices.",
      },
    ],
  },
  {
    id: "evaluation",
    title: "Evaluation & Results",
    description:
      "Automated and manual grading workflows with comprehensive result processing and analytics.",
    features: [
      {
        title: "Auto Grading (MCQ)",
        icon: "auto-grade",
        description:
          "Instant scoring for objective questions with negative marking and partial credit rules.",
      },
      {
        title: "Manual Evaluation",
        icon: "manual-eval",
        description:
          "Evaluators score descriptive answers with on-screen annotation and comment tools.",
      },
      {
        title: "Rubric-based Scoring",
        icon: "rubric",
        description:
          "Define scoring rubrics with criteria weights for consistent subjective evaluation.",
      },
      {
        title: "Moderation Workflow",
        icon: "moderation",
        description:
          "Multi-level review and moderation before final marks are locked and published.",
      },
      {
        title: "Result Processing",
        icon: "result-processing",
        description:
          "Calculate totals, ranks, percentiles, grades, and pass/fail status automatically.",
      },
      {
        title: "Revaluation Requests",
        icon: "revaluation",
        description:
          "Students apply for revaluation online with fee collection and status tracking.",
      },
      {
        title: "Result Publication",
        icon: "publish",
        description:
          "Controlled result release with SMS/email notifications and downloadable scorecards.",
      },
      {
        title: "Statistical Analytics",
        icon: "statistics",
        description:
          "Item analysis, difficulty index, discrimination index, and cohort comparison reports.",
      },
      {
        title: "Export & Integration",
        icon: "export",
        description:
          "Export results to Excel, PDF, or push directly to ERP/SIS gradebook modules.",
      },
      {
        title: "Merit Lists & Certificates",
        icon: "merit",
        description:
          "Generate merit lists, toppers reports, and printable certificates in bulk.",
      },
    ],
  },
];

export const oesAdvantages = [
  "Complete online examination platform — question bank, exam delivery, proctoring, and results in one system",
  "Support for MCQ, descriptive, subjective, and multimedia question types with flexible marking schemes",
  "AI-powered proctoring with webcam monitoring, tab-switch detection, and malpractice alerts",
  "Randomised question papers and shuffled options to ensure exam integrity at scale",
  "Auto-grading for objective questions with manual evaluation workflows for subjective answers",
  "Bulk student registration, hall ticket generation, and automated exam reminders",
  "Real-time live exam monitoring dashboard for invigilators and administrators",
  "Practice tests, mock exams, and adaptive testing modes for competitive exam preparation",
  "Integration with existing LMS, ERP, and SIS systems via APIs and SSO",
  "Cloud-hosted or on-premise deployment with mobile-responsive student interface",
];

export const oesWorkflowSteps = [
  {
    title: "Setup & Configuration",
    text: "Administrators configure institution details, user roles, exam policies, and security settings.",
  },
  {
    title: "Question Bank Creation",
    text: "Subject experts build and review question banks organised by topic, difficulty, and type.",
  },
  {
    title: "Exam Scheduling",
    text: "Exams are created with paper structure, timing, proctoring rules, and candidate assignments.",
  },
  {
    title: "Student Registration",
    text: "Candidates are registered, hall tickets generated, and pre-exam compatibility checks completed.",
  },
  {
    title: "Exam Conduct",
    text: "Students take timed exams with proctoring active while invigilators monitor live sessions.",
  },
  {
    title: "Evaluation & Results",
    text: "Answers are auto-graded or manually evaluated, results processed, and published to students.",
  },
];

export const oesPlatformApps = [
  {
    title: "Admin Dashboard",
    description:
      "Exam creation, scheduling, user management, live monitoring, reports, and institution-wide settings.",
    icon: "admin",
  },
  {
    title: "Examiner Portal",
    description:
      "Question bank management, paper setting, live invigilation, and manual evaluation for faculty.",
    icon: "examiner",
  },
  {
    title: "Student Portal",
    description:
      "Exam dashboard, hall tickets, live exam interface, results, analytics, and certificate download.",
    icon: "student",
  },
  {
    title: "Institution Reports",
    description:
      "Performance analytics, statistical reports, accreditation data, and export tools for management.",
    icon: "reports",
  },
];
